import { fabric } from 'fabric';
import type { AttachmentPoint } from '../types';
import { type Attachable } from '../objects/Connector';

// How far a connector is from the object it connects to.
export const STANDOFF_DISTANCE = 5;

interface AttachablePoint {
  attachmentPoint: AttachmentPoint;
  position: fabric.Point;
}

/**
 * Calculates the canvas coordinate position for an attachment point on a connectable object.
 */
export const calculateAttachmentPosition = (
  obj: Attachable,
  attachmentPoint: AttachmentPoint,
  { withStandoff = true }: { withStandoff: boolean }
) => {
  const controlDefinitions = obj.connectControlDefinition();
  const offsets = controlDefinitions[attachmentPoint];

  // Maybe the shape changed.
  if (!offsets) return null;

  const strokeWidth = obj.stroke ? obj.strokeWidth : 0;
  const standoff = withStandoff ? STANDOFF_DISTANCE : 0;

  return fabric.util.transformPoint(
    new fabric.Point(
      offsets.x * (obj.width + strokeWidth / obj.scaleX) +
        (offsets.normal.x * standoff) / obj.scaleX,
      offsets.y * (obj.height + strokeWidth / obj.scaleY) +
        (offsets.normal.y * standoff) / obj.scaleY
    ),
    obj.calcTransformMatrix()
  );
};

/**
 * Return all of the points where a connector can attach to the object the cursor
 * is over.
 */
export const fetchAttachmentPoints = (
  overObject: Attachable | undefined | null,
  { withStandoff }: { withStandoff: boolean }
) => {
  if (!overObject) {
    return [];
  }

  return overObject
    .attachmentPoints?.()
    .map(attachmentPoint => {
      return {
        attachmentPoint,
        position: calculateAttachmentPosition(overObject, attachmentPoint, {
          withStandoff,
        }),
      };
    })
    .filter(
      (attachment): attachment is AttachablePoint => !!attachment.position
    );
};
